import { useMemo } from "react";

export type RiskBand = "Green" | "Yellow" | "Red";

const BAND_ACCENT: Record<RiskBand, string> = {
  Green: "#5BB280",
  Yellow: "#E0B52E",
  Red: "#B24B7C",
};

type TrendLineProps = {
  values: number[];
  statuses: RiskBand[];
  thresholds: {
    greenMax: number;
    yellowMax: number;
    maxScore: number;
  };
};

export default function TrendLine({
  values,
  statuses,
  thresholds,
}: TrendLineProps) {
  const width = 360;
  const height = 120;
  const paddingX = 12;
  const paddingY = 12;
  const defaultY = height / 2;

  const chart = useMemo(() => {
    const yForValue = (value: number) => {
      const minValue = Math.min(0, ...values);
      const maxValue = Math.max(
        thresholds.maxScore,
        ...values,
        thresholds.greenMax,
        thresholds.yellowMax,
      );

      if (maxValue === minValue) return defaultY;

      const normalized = (value - minValue) / (maxValue - minValue);
      return height - paddingY - normalized * (height - paddingY * 2);
    };

    if (values.length === 0) {
      return {
        points: [] as Array<{ x: number; y: number }>,
        yForValue,
      };
    }

    const xStep =
      values.length > 1 ? (width - paddingX * 2) / (values.length - 1) : 0;

    const points = values.map((value, index) => ({
      x: paddingX + index * xStep,
      y: yForValue(value),
    }));

    return {
      points,
      yForValue,
    };
  }, [
    defaultY,
    height,
    paddingX,
    paddingY,
    thresholds.greenMax,
    thresholds.maxScore,
    thresholds.yellowMax,
    values,
    width,
  ]);

  if (values.length === 0) {
    return <p className="text-sm text-[#7F7F7F]">No trend data available.</p>;
  }

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-30 w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {[thresholds.greenMax, thresholds.yellowMax, thresholds.maxScore].map(
          (threshold, index) => {
            const y = chart.yForValue(threshold);

            return (
              <g key={`baseline-${threshold}-${index}`}>
                <line
                  x1={paddingX}
                  y1={y}
                  x2={width - paddingX}
                  y2={y}
                  stroke="#D5DADC"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                />
                <text
                  x={width - paddingX}
                  y={y - 4}
                  textAnchor="end"
                  className="fill-[#7F7F7F] text-[10px] font-medium"
                >
                  {threshold}
                </text>
              </g>
            );
          },
        )}
        {chart.points.slice(1).map((point, index) => {
          const previous = chart.points[index];
          const status = statuses[index + 1] || statuses[index] || "Green";

          return (
            <line
              key={`segment-${previous.x}-${previous.y}-${point.x}-${point.y}`}
              x1={previous.x}
              y1={previous.y}
              x2={point.x}
              y2={point.y}
              stroke={BAND_ACCENT[status]}
              strokeWidth={3}
              strokeLinecap="round"
            />
          );
        })}
        {chart.points.map((point, index) => (
          <circle
            key={`${point.x}-${point.y}-${index}`}
            cx={point.x}
            cy={point.y}
            r={index === chart.points.length - 1 ? 4.5 : 2.5}
            fill={BAND_ACCENT[statuses[index] || "Green"]}
          />
        ))}
      </svg>
      <div className="mt-3 flex items-center justify-between text-xs text-[#7F7F7F]">
        <span>Earlier</span>
        <span>
          Latest:{" "}
          <span className="font-semibold text-[#2B4752]">{values.at(-1)}</span>
        </span>
      </div>
    </div>
  );
}
